import { useAuth } from "@allergy123/auth-client/react";
import { Container, Status } from "@allergy123/ui";
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type RefObject } from "react";
import { Link, useNavigate } from "react-router";
import { useSession } from "../session";
import { browserCues } from "./cues";
import { createTimerStore, dueAnnouncement, mmss, READ_LABELS, type RunningTimer } from "./timers";

/** The tab's running test timers (memory only; see timers.ts). */
export const clinicTimers = createTimerStore({ cues: browserCues });

export function useRunningTimers(): readonly RunningTimer[] {
  return useSyncExternalStore(clinicTimers.subscribe, clinicTimers.list, clinicTimers.list);
}

/** The latest due time plus the grace, for the idle sign-out; null when no timer is on the bar. */
export function useTimerHold(): number | null {
  return useSyncExternalStore(clinicTimers.subscribe, clinicTimers.holdUntil, clinicTimers.holdUntil);
}

/**
 * Every sign-out (the button, an idle sign-out, another tab's) ends this tab's timers and their pending cues with the
 * session. Mounted outside the signed-in screens, which are already gone while the sign-out leaves.
 */
export function ClearTimersOnSignOut() {
  const { status } = useAuth();
  useEffect(() => {
    if (status === "signing-out" || status === "signed-out") clinicTimers.clear();
  }, [status]);
  return null;
}

/** The bar's clock. Each tick also cues any read the wall clock says is due (see `check` in timers.ts). */
function useNow(ticking: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    if (!ticking) return;
    const t = setInterval(() => {
      clinicTimers.check();
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(t);
  }, [ticking]);
  return now;
}

/** Browser timers do not run while the computer sleeps or the tab is frozen: cue what came due as soon as the tab is back. */
function useCatchUp() {
  useEffect(() => {
    const check = () => clinicTimers.check();
    document.addEventListener("visibilitychange", check);
    document.addEventListener("resume", check);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);
    return () => {
      document.removeEventListener("visibilitychange", check);
      document.removeEventListener("resume", check);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", check);
    };
  }, []);
}

/** Keeps a focused control clear of the sticky bar (WCAG 2.4.11): the page's bottom scroll padding follows the bar's height. */
function useScrollPadding(ref: RefObject<HTMLElement | null>, shown: boolean) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!shown || !el) return;
    const root = document.documentElement;
    const set = () => {
      root.style.scrollPaddingBottom = `${el.offsetHeight}px`;
    };
    set();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(set);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.scrollPaddingBottom = "";
    };
  }, [ref, shown]);
}

/**
 * The running-timers bar: stays at the foot of every screen while a timer this tab started is on it, counts down each
 * read, turns into the visual cue when one comes due, and links back to the encounter. It shows only what the
 * encounter screens already show (the patient's name); the sound and the OS notification carry nothing identifying.
 * A timer of another practice than the one in scope (after a switch) shows the practice instead of the patient, and
 * its link switches back to that practice before opening the encounter.
 */
export function RunningTimersBar() {
  const list = useRunningTimers();
  const s = useSession();
  const navigate = useNavigate();
  const running = list.some((t) => t.reads.some((r) => !r.done));
  const alerting = list.filter((t) => t.alerting);
  const now = useNow(running);
  const bar = useRef<HTMLElement>(null);
  useCatchUp();
  useScrollPadding(bar, list.length > 0);

  // The visual cue also reaches the tab strip, so a read due is seen from another tab. Nothing identifying in it.
  useEffect(() => {
    if (alerting.length === 0) return;
    const base = document.title;
    document.title = `Read due · ${base}`;
    return () => {
      document.title = base;
    };
  }, [alerting.length]);

  if (list.length === 0) return null;
  return (
    <aside ref={bar} aria-label="Running test timers" className="sticky bottom-0 z-30 border-t border-rule-strong bg-card">
      <Container className="flex flex-wrap items-stretch gap-3 py-3">
        <p className="sr-only" role="status">
          {dueAnnouncement(alerting.length)}
        </p>
        {list.map((t) => {
          const finished = t.reads.every((r) => r.done);
          const here = t.organizationId === s.organizationId;
          const practice = s.me.organizations.find((o) => o.id === t.organizationId)?.name ?? "another practice";
          return (
            <div key={t.encounterId} className={`flex min-w-[16rem] flex-wrap items-center gap-x-4 gap-y-2 rounded-md border px-3 py-2 ${t.alerting ? "border-2 border-status-warning bg-card-tinted" : "border-rule-subtle"}`}>
              <div className="flex flex-col">
                <span className="text-small font-semibold text-anchor">{here ? t.label : `A test at ${practice}`}</span>
                <span className="text-small tabular-nums text-ink-secondary">
                  {t.reads.map((r) => `${READ_LABELS[r.key]} ${r.done ? "due" : mmss(r.dueAt - now)}`).join(" · ")}
                </span>
              </div>
              {t.alerting && <Status kind="warning" label={finished ? "All reads due" : "Read due"} className="motion-safe:animate-pulse" />}
              <div className="flex items-center gap-3 text-small">
                <Link
                  to={`/encounters/${t.encounterId}`}
                  onClick={(e) => {
                    clinicTimers.acknowledge(t.encounterId);
                    if (here) return;
                    e.preventDefault();
                    s.switchOrganization(t.organizationId);
                    navigate(`/encounters/${t.encounterId}`);
                  }}
                  className="font-medium"
                >
                  {here ? "Open" : "Switch practice and open"}
                </Link>
                {t.alerting && (
                  <button type="button" className="font-medium text-text-brand underline underline-offset-4" onClick={() => clinicTimers.acknowledge(t.encounterId)}>
                    OK
                  </button>
                )}
                {finished && (
                  <button type="button" className="font-medium text-text-brand underline underline-offset-4" onClick={() => clinicTimers.dismiss(t.encounterId)}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </Container>
    </aside>
  );
}
